import { Box, Card, CardContent, Grid, Link, makeStyles, Typography } from '@material-ui/core';
import { FunctionComponent, useEffect } from 'react';
import drSimoneLogo from '../../../assets/images/drPeters.png';
import nervaScreenshots from '../../../assets/images/nervaScreenshots.png';
import checklist from '../../../assets/svgs/checklist.svg';
import download from '../../../assets/svgs/download.svg';
import graph from '../../../assets/svgs/graph.svg';
import hyperlinkWithGreenBackground from '../../../assets/svgs/hyperlinkWithGreenBackground.svg';
import mindAndGutLogo from '../../../assets/svgs/logos/mind-and-gut-logo.svg';
import monashLogo from '../../../assets/svgs/logos/monash-logo.svg';
import nervaLogoWithBackground from '../../../assets/svgs/nervaLogoWithBackground.svg';
import tick from '../../../assets/svgs/tick.svg';
import wateringCan from '../../../assets/svgs/wateringCan.svg';
import { CONNECT_SCREENS } from '../../../constants';
import { Analytics } from '../../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../../helpers/analytics/AnalyticsEvent';
import { HCP_REFERRAL_COUPON_DISCOUNT_PERCENT } from '../../Auth/PatientThankYouScreen';

const useStyles = makeStyles((theme) => ({
  cardRoot: { height: '100%', position: 'relative' },
  boldBodyCopy: { fontFamily: 'CircularXX-Book, sans-serif', fontWeight: 700 },
  profile: { borderTopLeftRadius: '50px' },
  companies: {
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginRight: 0,
      marginLeft: '120px',
    },
  },
  monashLogo: {
    marginTop: '5px',
    [theme.breakpoints.down('xs')]: {
      marginRight: '25px',
      marginTop: 0,
    },
  },
  taskList: {
    marginTop: 0,
    paddingLeft: 15,
  },
}));

export const DetailsAndJourney: FunctionComponent = () => {
  const classes = useStyles();

  useEffect(function trackAnalytics() {
    Analytics.track({
      event: AnalyticsEventName.h_screen_viewed,
      screen: CONNECT_SCREENS.NERVA_INFO,
    });
  }, []);

  return (
    <Grid container item justifyContent="space-between" spacing={2} alignItems="stretch">
      <Grid item xs={12} md={6}>
        <Card elevation={1} classes={{ root: classes.cardRoot }}>
          <CardContent>
            <Box paddingLeft="25px" paddingRight="25px" marginBottom="30px" marginTop="45px">
              <Typography variant="h4">Product Overview</Typography>
            </Box>
            <Box maxWidth="663px" paddingLeft="25px" paddingRight="25px" marginBottom="30px">
              <Typography>
                Nerva is a 6-week gut-directed hypnotherapy program for IBS that is accessible via
                an easy-to-use app. With daily use, most users can expect to see results within 4-6
                weeks.
              </Typography>
            </Box>
            <Box paddingLeft="25px">
              <img src={nervaScreenshots} alt="app logo" width="312px" />
            </Box>
            <Box marginLeft="25px" marginBottom="32px">
              <Typography color="primary" variant="h5">
                Aim
              </Typography>
              <Box maxWidth="476px">
                <Typography>
                  To teach patients how to rewire their gut-brain connection and improve their IBS
                  symptom management.
                </Typography>
              </Box>
            </Box>
            <Box marginLeft="25px" marginBottom="32px">
              <Typography color="primary" variant="h5">
                Program
              </Typography>
              <Box maxWidth="550px">
                <Typography>
                  A 6-week at-home hypnotherapy program with daily 15-minute sessions.
                </Typography>
              </Box>
            </Box>
            <Box marginLeft="25px" marginBottom="32px">
              <Typography color="primary" variant="h5">
                Evidence
              </Typography>
              <Box maxWidth="476px">
                <Typography>
                  Gut-directed hypnotherapy has been shown to reduce IBS symptoms by 70-80% in the
                  majority of people.
                </Typography>
              </Box>
            </Box>
            <Box marginLeft="25px" marginBottom="45px" height="200px">
              <Typography color="primary" variant="h5">
                Cost
              </Typography>
              <Box maxWidth="476px">
                <Typography>7-day trial, then USD $199 every year.</Typography>
                <Typography variant="subtitle2">
                  Your patients will receive a {HCP_REFERRAL_COUPON_DISCOUNT_PERCENT} discount if
                  they use your referral link.
                </Typography>
              </Box>
            </Box>
            <Box
              bgcolor="#F9F9F9"
              position="absolute"
              left={0}
              right={0}
              bottom={0}
              paddingTop="25px"
              paddingBottom="25px"
              className={classes.profile}
              display="flex"
            >
              <Grid container>
                <Grid item xs={12} sm={8}>
                  <Box display="flex" alignItems="center" marginLeft="25px">
                    <img src={drSimoneLogo} alt="Doctor Simone Peters" width="82px" height="82px" />
                    <Box display="flex" flexDirection="column" marginLeft="15px">
                      <Typography variant="h5" color="primary">
                        Created By:
                      </Typography>
                      <Typography variant="h6">Dr. Simone Peters</Typography>
                    </Box>
                  </Box>
                  <Box />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    display="flex"
                    marginRight="25px"
                    marginLeft="25px"
                    flexDirection="column"
                    className={classes.companies}
                    alignItems="flex-end"
                  >
                    <Box marginBottom="10px" className={classes.monashLogo}>
                      <img src={monashLogo} alt="Monash University logo" width="100px" />
                    </Box>
                    <img src={mindAndGutLogo} alt="Mind and Gut clinic logo" height="30px" />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card elevation={1} classes={{ root: classes.cardRoot }}>
          <CardContent>
            <Box paddingLeft="25px" paddingRight="25px" marginBottom="30px" marginTop="45px">
              <Typography variant="h4">Journey</Typography>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              maxWidth="663px"
              paddingLeft="25px"
              paddingRight="25px"
              marginBottom="30px"
            >
              <Box marginBottom="25px">
                <Typography color="primary" variant="h5">
                  Linking Stage
                </Typography>
              </Box>
              <Box display="flex">
                <Box marginRight="17px">
                  <img
                    src={nervaLogoWithBackground}
                    alt="document icon"
                    width="40px"
                    height="40px"
                  />
                </Box>
                <Typography>
                  <span className={classes.boldBodyCopy}>Refer Nerva - </span>
                  Copy your referral link, and send it to your patient.
                </Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              maxWidth="663px"
              paddingLeft="25px"
              paddingRight="25px"
              marginBottom="55px"
            >
              <Box marginRight="17px">
                <img
                  src={hyperlinkWithGreenBackground}
                  alt="hyperlink icon"
                  width="40px"
                  height="40px"
                />
              </Box>
              <Typography>
                <span className={classes.boldBodyCopy}>Create account - </span>
                The patient opens the link you sent to accept your referral and create an account.
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              maxWidth="663px"
              paddingLeft="25px"
              paddingRight="25px"
              marginBottom="30px"
            >
              <Box marginBottom="25px">
                <Typography color="primary" variant="h5">
                  Core Program Stage
                </Typography>
              </Box>
              <Box display="flex">
                <Box marginRight="17px">
                  <img src={checklist} alt="checklist icon" width="40px" height="40px" />
                </Box>
                <Typography>
                  <span className={classes.boldBodyCopy}>Assessment - </span>
                  They complete an online assessment and are offered a 7 day trial.
                </Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              maxWidth="663px"
              paddingLeft="25px"
              paddingRight="25px"
              marginBottom="30px"
            >
              <Box marginRight="17px">
                <img src={download} alt="download icon" width="40px" height="40px" />
              </Box>
              <Typography>
                <span className={classes.boldBodyCopy}>Start program - </span>
                They download the Nerva app and begin the program. We’ll then send you a letter with
                their initial symptom report.
              </Typography>
            </Box>
            <Box
              display="flex"
              maxWidth="663px"
              paddingLeft="25px"
              paddingRight="25px"
              marginBottom="55px"
            >
              <Box marginRight="21px">
                <img src={tick} alt="tick icon" width="35px" height="35px" />
              </Box>
              <Typography>
                <span className={classes.boldBodyCopy}>Six-week core program - </span>
                They will have daily tasks and exercises to complete every week.
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              maxWidth="663px"
              paddingLeft="25px"
              paddingRight="25px"
              marginBottom="30px"
            >
              <Box marginBottom="25px">
                <Typography color="primary" variant="h5">
                  Post Program Stage
                </Typography>
              </Box>
              <Box display="flex">
                <Box marginRight="17px">
                  <img src={graph} alt="graph icon" width="40px" height="40px" />
                </Box>
                <Typography>
                  <span className={classes.boldBodyCopy}>Finish program - </span>
                  We’ll send you a letter with their symptom results.
                </Typography>
              </Box>
            </Box>

            <Box
              display="flex"
              maxWidth="663px"
              paddingLeft="25px"
              paddingRight="25px"
              marginBottom="45px"
            >
              <Box marginRight="17px">
                <img src={wateringCan} alt="watering can icon" width="40px" height="40px" />
              </Box>
              <Typography>
                <span className={classes.boldBodyCopy}>Maintenance - </span>
                They can continue listening at their own pace to reinforce results.
              </Typography>
            </Box>

            <Box maxWidth="663px" paddingLeft="25px" paddingRight="25px">
              <Typography variant="body2">
                Need any help or have feedback? Contact{' '}
                <b>
                  <Link href="mailto: healthcare@mindsethealth.com" color="primary">
                    healthcare@mindsethealth.com
                  </Link>
                </b>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
